/**
 * Admin auth: keys used by AdminLogin / admin API client.
 * Do not use localStorage.clear() — candidate flows share the same origin.
 * MSAL manages its own cache keys; we only track what we set ourselves.
 *
 * Logout matches ReachOut: a localStorage marker survives new tabs, and
 * Microsoft sign-in after logout must not silent-restore the previous user.
 */
import { adminAuthAPI } from '../services/api';

export const ADMIN_STORAGE_KEYS = ['token', 'admin'];

/** sessionStorage copy — same-tab fast path. */
export const LOGOUT_FLAG_KEY = 'wizetok_logged_out';
/** Survives new tabs. sessionStorage alone was why logout still auto-opened the previous user. */
const LOGGED_OUT_KEY = 'wizetok_logged_out_ls';
const SSO_PENDING_KEY = 'wizetok_sso_pending';
const BRIDGE_ATTEMPTED_KEY = 'wizetok_reachout_bridge_attempted';

function setKey(store, key, value) {
  try {
    store.setItem(key, value);
  } catch {
    // ignore
  }
}

function clearKey(store, key) {
  try {
    store.removeItem(key);
  } catch {
    // ignore
  }
}

export function clearAdminSession() {
  ADMIN_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

/** Wipe MSAL cache entries without interactive popup (popup often hangs / is blocked). */
export function clearMsalCache() {
  [sessionStorage, localStorage].forEach((store) => {
    Object.keys(store)
      .filter((k) => k.startsWith('msal.') || k.includes('msal'))
      .forEach((k) => store.removeItem(k));
  });
}

export function markLoggedOut() {
  const now = String(Date.now());
  setKey(sessionStorage, LOGOUT_FLAG_KEY, '1');
  setKey(localStorage, LOGGED_OUT_KEY, now);
  clearKey(sessionStorage, 'wizetok_sso_redirect');
  clearKey(sessionStorage, BRIDGE_ATTEMPTED_KEY);
  clearKey(sessionStorage, SSO_PENDING_KEY);
}

export function clearLogoutFlag() {
  clearKey(sessionStorage, LOGOUT_FLAG_KEY);
  clearKey(localStorage, LOGGED_OUT_KEY);
  clearKey(sessionStorage, SSO_PENDING_KEY);
}

export function isLoggedOutLocally() {
  try {
    return sessionStorage.getItem(LOGOUT_FLAG_KEY) != null
      || localStorage.getItem(LOGGED_OUT_KEY) != null;
  } catch {
    return false;
  }
}

export function markSsoPending() {
  setKey(sessionStorage, SSO_PENDING_KEY, '1');
}

export function isSsoPending() {
  try {
    return sessionStorage.getItem(SSO_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearSsoPending() {
  clearKey(sessionStorage, SSO_PENDING_KEY);
}

function isAdminAppPath(pathname) {
  const path = String(pathname || '');
  if (path === '/admin/login' || path.startsWith('/admin/login/')) return false;
  return path === '/admin' || path.startsWith('/admin/');
}

export function blockAdminRouteIfLoggedOut() {
  if (!isLoggedOutLocally() || isSsoPending()) return false;
  if (!isAdminAppPath(window.location.pathname || '')) return false;
  try {
    window.location.replace('/admin/login');
  } catch {
    window.location.href = '/admin/login';
  }
  return true;
}

export function installAuthNavigationGuards() {
  if (typeof window === 'undefined') return;
  if (window.__wizetokAuthGuardsInstalled) return;
  window.__wizetokAuthGuardsInstalled = true;
  const guard = () => {
    blockAdminRouteIfLoggedOut();
  };
  window.addEventListener('pageshow', guard);
  window.addEventListener('popstate', guard);
}

/**
 * ReachOut-style logout: persist logged-out, clear local session + MSAL cache, hard-redirect.
 * Do not await logoutPopup — it blocks and AdminLogin would silent-login again.
 */
export async function signOut() {
  markLoggedOut();

  try {
    await adminAuthAPI.logout();
  } catch {
    // Best-effort server logout; local clear still proceeds.
  }

  clearAdminSession();
  clearMsalCache();

  try {
    window.history.replaceState({ wizetokLoggedOut: true }, '', '/admin/login');
  } catch {
    // ignore
  }
  window.location.replace('/admin/login');
}

if (typeof window !== 'undefined') {
  installAuthNavigationGuards();
  blockAdminRouteIfLoggedOut();
}
